import { PayloadAction, createSlice } from "@reduxjs/toolkit"
import { Note } from "../../../../shared/lib/types/notes-model/notes-model"

export type NoteChanges = Partial<Pick<Note, 'title' | 'description'>>;

export type NotesStateInit = {
    removedIds: Record<string, true>,
    draftEpochs: Record<string, string>,
    drafts: Record<string, NoteChanges>,
    revisions: Record<string, number>,
    saveErrors: Record<string, string>,
    entities: Note[],
    isLoading: 'idle' | 'pending' | 'success' | 'error',
    error: string | null 
}

export const initialStateNotes: NotesStateInit = {
    removedIds: {},
    draftEpochs: {},
    drafts: {},
    revisions: {},
    saveErrors: {},
    entities: [],
    isLoading: 'idle',
    error: null
}

export const notesSlice = createSlice({
    name: 'notes',
    initialState: initialStateNotes,
    selectors: {
        selectAllNotes: (state) => state.entities,
        selectNoteById: (state, id: string) => state.entities.find((el) => el.id === id),
        selectTypingNote: (state) => state.entities.find((el) => el.isTyping === 'typing'),
        getLoading: (state) => state.isLoading,
        getNotesCount: (state) => state.entities.length 
    },
    reducers: {
        editDraft: (state, action: PayloadAction<{id: string; data: NoteChanges; epoch: string}>) => {
            const {id, data} = action.payload;
            state.draftEpochs[id] = action.payload.epoch;
            state.drafts[id] = {...state.drafts[id], ...data};
            state.revisions[id] = (state.revisions[id] ?? 0) + 1;
            delete state.saveErrors[id];
            const note = state.entities.find(item => item.id === id);
            if (note) Object.assign(note, data, {isTyping: 'typing'});
        },
        dismissSavedStatus: (state, action: PayloadAction<{id: string}>) => {
            const note = state.entities.find(item => item.id === action.payload.id);
            if (note?.isTyping === 'saved') note.isTyping = 'idle';
        },
        saveConfirmed: (state, action: PayloadAction<{id: string; revision: number}>) => {
            const {id, revision} = action.payload;
            // An acknowledgement for an older snapshot cannot clear newer edits.
            if (state.revisions[id] !== revision) return;
            delete state.drafts[id];
            delete state.draftEpochs[id];
            delete state.saveErrors[id];
            state.revisions[id]++;
            const note = state.entities.find(item => item.id === id);
            if (note) note.isTyping = 'saved';
        },
        saveFailed: (state, action: PayloadAction<{id: string; revision: number}>) => {
            const {id, revision} = action.payload;
            if (state.revisions[id] !== revision || !state.drafts[id]) return;
            state.saveErrors[id] = 'Не удалось сохранить заметку. Черновик сохранён до повторной отправки.';
            const note = state.entities.find(item => item.id === id);
            if (note) note.isTyping = 'idle';
        },
        updateNote: (state, action: PayloadAction<{ id: string; data: Partial<Pick<Note, 'title' | 'description'>> }>) => {
            const note = state.entities.find((item) => item.id === action.payload.id);
            if (note) {
                if (action.payload.data.title !== undefined) note.title = action.payload.data.title;
                if (action.payload.data.description !== undefined) note.description = action.payload.data.description;
            }
        },
        loadingSuccess: (state) => {
            state.isLoading = 'success'
        },
        loadingIdle: (state) => {
            state.isLoading = 'idle'
        },
        loadAllSuccess: (state, action: PayloadAction<{data: Note[]; revisions?: Record<string, number>}>) => {
            state.entities = action.payload.data.filter(note => !state.removedIds?.[note.id]).map(note => {
                const previous = state.entities.find(item => item.id === note.id);
                const changed = action.payload.revisions &&
                    (action.payload.revisions[note.id] ?? 0) !== (state.revisions[note.id] ?? 0);
                const merged = {...(changed && previous ? previous : note), ...state.drafts[note.id]};
                if (state.drafts[note.id]) merged.isTyping = previous?.isTyping ?? 'typing';
                return merged;
            })
            state.isLoading = 'success'
        },
        discardDraft: (state, action: PayloadAction<{id: string}>) => {
            delete state.drafts[action.payload.id];
            delete state.draftEpochs[action.payload.id];
            delete state.saveErrors[action.payload.id];
        },
        restoreNote: (state, action: PayloadAction<{note: Note, index: number}>) => {
            const {note, index} = action.payload;
            if (state.removedIds) delete state.removedIds[note.id];
            state.entities = state.entities.filter(item => item.id !== note.id);
            state.entities.splice(index, 0, {...note, ...state.drafts[note.id]});
        },
        removeNote: (state, action: PayloadAction<{id: string}>) => {
            state.removedIds ??= {};
            state.removedIds[action.payload.id] = true;
            state.entities = state.entities.filter(el => el.id !== action.payload.id);
            state.revisions[action.payload.id] = (state.revisions[action.payload.id] ?? 0) + 1;
        },
        addNote: (state, action: PayloadAction<{data: Note}>) => {
            state.entities.push(action.payload.data)            
        },
        markerTypingStatus: (state, action: PayloadAction<{id: string, mode: 'idle' | 'typing' | 'saved'}>) => {
            state.entities.forEach(el => {
                if (el.id === action.payload.id) {
                    el.isTyping = action.payload.mode
                } else {
                    el.isTyping = 'idle';
                }
            })
        },
        dublicateNote: (state, action: PayloadAction<{id: string, newId: string}>) => {
            const getCopying = state.entities.find(el => el.id === action.payload.id)
            if (getCopying) {
                const copy: Note = {
                    ...getCopying,
                    id: action.payload.newId
                }
                state.entities.push(copy)
            }
        },

    },
    extraReducers: builder => {
        builder.addCase('FETCH_DATA_USER_ACCOUNT_LOGOUT', () => initialStateNotes);
    }
})
